import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Card, Button, TextField, InputAdornment, Chip, IconButton,
  Typography, FormControl, InputLabel, Select, MenuItem, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Drawer,
} from '@mui/material';
import ErpDataGrid, { GeneralItemsGridFrame } from '../../components/common/ErpDataGrid';
import ListDetailOpenModeBar from '../../components/common/ListDetailOpenModeBar';
import ListRowActionsBar from '../../components/common/ListRowActionsBar';
import useListDetailOpenMode from '../../hooks/useListDetailOpenMode';
import AccountDetails from './AccountDetails';
import {
  Add as AddIcon, Search as SearchIcon,
  Edit as EditIcon, Delete as DeleteIcon, Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { standardAuditColumns } from '../../utils/auditGridColumns';
import { copyAppPathToClipboard, openAppPathInNewTab } from '../../utils/listScreenHelpers';

const AccountsList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const { mode, persistMode, popupId, setPopupId, sideId, setSideId, openRow } = useListDetailOpenMode('accounts');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => String(searchParams.get('search') || ''));
  const [filters, setFilters] = useState({ account_type: '', status: '' });
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10, total: 0 });
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/accounts', {
        params: { page: pagination.page + 1, limit: pagination.pageSize, search, ...filters }
      });
      setAccounts(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.pagination.total }));
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, search, filters]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  useEffect(() => {
    const q = String(searchParams.get('search') || '');
    if (q) {
      setSearch(q);
    }
  }, [searchParams]);

  const handleDelete = async () => {
    if (!selectedAccount) return;
    try {
      await api.delete(`/accounts/${selectedAccount.id}`);
      toast.success('Account deleted successfully');
      fetchAccounts();
    } catch (error) { toast.error('Failed to delete'); }
    setDeleteDialog(false);
    setSelectedAccount(null);
  };

  const typeColors = { client: '#6366f1', vendor: '#ec4899', both: '#10b981', hosting_company: '#0ea5e9' };
  const typeLabels = { client: 'CLIENT', vendor: 'VENDOR', both: 'BOTH', hosting_company: 'HOSTING' };
  const statusColors = {
    active: '#10b981',
    inactive: '#6b7280',
    prospect: '#f59e0b',
    archived: '#7c3aed',
    deleted: '#b45309'
  };

  const statusDisplay = (row) => {
    if (row.archived_at) return { label: 'Archived', key: 'archived' };
    if (row.status === 'deleted' || row.is_marked_for_deletion) return { label: 'Deleted', key: 'deleted' };
    const s = row.status || 'active';
    return { label: s.charAt(0).toUpperCase() + s.slice(1), key: s };
  };

  const columns = [
    { field: 'company_name', headerName: 'Company Name', flex: 1, minWidth: 200 },
    {
      field: 'account_code',
      headerName: 'Reference',
      width: 180,
      minWidth: 140,
      hideable: false,
      sortable: true,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace" sx={{ fontWeight: 600 }}>
          {params.value ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'account_type', headerName: 'Type', width: 118,
      renderCell: (params) => (
        <Chip
          label={typeLabels[params.value] || params.value?.toUpperCase()}
          size="small"
          sx={{
            bgcolor: `${typeColors[params.value] || '#64748b'}20`,
            color: typeColors[params.value] || '#64748b',
            fontWeight: 500,
            fontSize: '0.7rem',
          }}
        />
      )
    },
    { field: 'industry', headerName: 'Industry', width: 150 },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => {
        const { label, key } = statusDisplay(params.row);
        const color = statusColors[key] || statusColors.inactive;
        return (
          <Chip
            label={label}
            size="small"
            sx={{ bgcolor: `${color}20`, color, fontWeight: 500, fontSize: '0.7rem' }}
          />
        );
      }
    },
    {
      field: 'contacts', headerName: 'Contacts', width: 150,
      renderCell: (params) => {
        const contacts = params.row.Contacts || [];
        if (contacts.length === 0) return <Typography variant="body2" color="text.secondary">-</Typography>;
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {contacts[0].first_name} {contacts[0].last_name}
            </Typography>
            {contacts.length > 1 && (
              <Typography variant="caption" color="text.secondary">+{contacts.length - 1} more</Typography>
            )}
          </Box>
        );
      }
    },
    ...standardAuditColumns(),
    {
      field: 'actions', headerName: 'Actions', width: 260, sortable: false,
      renderCell: (params) => {
        const row = params.row;
        const detailPath = `/accounts/${row.id}`;
        return (
          <ListRowActionsBar
            menuItems={[
              {
                label: 'Open in new tab',
                icon: <OpenInNewIcon fontSize="small" />,
                onClick: () => openAppPathInNewTab(detailPath),
              },
              {
                label: 'Copy link',
                icon: <CopyIcon fontSize="small" />,
                onClick: () => copyAppPathToClipboard(detailPath),
              },
            ]}
          >
            {hasPermission('accounts_modify') && (
              <Tooltip title="Edit">
                <IconButton size="small" color="primary" onClick={() => navigate(`${detailPath}/edit`)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="View"><IconButton size="small" onClick={() => navigate(detailPath)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Open in new tab for print or Save as PDF">
              <IconButton size="small" color="secondary" onClick={() => openAppPathInNewTab(detailPath)}><PdfIcon fontSize="small" /></IconButton>
            </Tooltip>
            {hasPermission('accounts_delete') && (
              <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => { setSelectedAccount(row); setDeleteDialog(true); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
            )}
          </ListRowActionsBar>
        );
      }
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Accounts</Typography>
          <Typography variant="body2" color="text.secondary">Manage client and vendor accounts</Typography>
        </Box>
        {hasPermission('accounts_add') && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/accounts/new')}
            sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>Add Account</Button>
        )}
      </Box>

      <Card sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField placeholder="Search accounts..." value={search} onChange={(e) => setSearch(e.target.value)} size="small" sx={{ minWidth: 300 }}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment>) }} />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select value={filters.account_type} label="Type" onChange={(e) => setFilters({ ...filters, account_type: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="client">Client</MenuItem>
              <MenuItem value="vendor">Vendor</MenuItem>
              <MenuItem value="both">Both</MenuItem>
              <MenuItem value="hosting_company">Hosting Company *</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filters.status} label="Status" onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="prospect">Prospect</MenuItem>
              <MenuItem value="deleted">Deleted</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
          <ListDetailOpenModeBar value={mode} onChange={persistMode} sx={{ flex: '1 1 auto' }} />
        </Box>
      </Card>

      <Card sx={{ overflow: 'hidden' }}>
        <GeneralItemsGridFrame>
          <ErpDataGrid
            listApplet
            columnOrderStorageKey="general-items-accounts"
            rows={accounts}
            columns={columns}
            loading={loading}
            paginationMode="server"
            rowCount={pagination.total}
            paginationModel={{ page: pagination.page, pageSize: pagination.pageSize }}
            onPaginationModelChange={(model) => setPagination({ ...pagination, ...model })}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            autoHeight={false}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
            onRowClick={(params) => openRow(params.row, (r) => `/accounts/${r.id}`)}
          />
        </GeneralItemsGridFrame>
      </Card>

      <Drawer
        anchor="right"
        open={mode === 'side' && Boolean(sideId)}
        onClose={() => setSideId(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560, md: 720 }, maxWidth: '100vw' } }}
      >
        <Box sx={{ height: '100%', overflow: 'auto', p: { xs: 1.5, sm: 2 } }}>
          {sideId ? (
            <AccountDetails detailId={sideId} embedded onCloseEmbedded={() => setSideId(null)} />
          ) : null}
        </Box>
      </Drawer>

      <Dialog fullWidth maxWidth="lg" open={Boolean(popupId)} onClose={() => setPopupId(null)} scroll="paper">
        <DialogContent sx={{ p: 0 }}>
          {popupId ? (
            <AccountDetails detailId={popupId} embedded onCloseEmbedded={() => setPopupId(null)} />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialog} onClose={() => { setDeleteDialog(false); setSelectedAccount(null); }}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent><Typography>Are you sure you want to delete "{selectedAccount?.company_name}"? This cannot be undone.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteDialog(false); setSelectedAccount(null); }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountsList;
